import { useState } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';

import { fCurrency } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { ColorPreview } from 'src/components/color-utils';
import { ShowMoreLessText } from 'src/components/text-utils';

// ----------------------------------------------------------------------

export function ProductDescription({ product, preview, reviewHook, openReviews }) {
  const { t } = useTranslate();

  const [showMore, setShowMoreLess] = useState(false);

  const {
    id,
    name,
    price,
    colors,
    country,
    shipping,
    newLabel,
    saleLabel,
    priceSale,
    totalRating,
    numberFormat,
    shortDescription,
  } = product;

  return (
    <Stack className={mergeClasses('product__description', { show__more: showMore })}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <ColorPreview colors={colors} sx={{ ml: 0.7 }} />

        <Stack
          spacing={0.5}
          direction="row"
          sx={{ typography: { xs: 'h5', sm: 'h6', lg: 'subtitle1' } }}
        >
          {saleLabel?.enabled && (
            <Box component="span" sx={{ opacity: 0.7, textDecoration: 'line-through' }}>
              {fCurrency(price, numberFormat)}
            </Box>
          )}

          <Box component="span">{fCurrency(priceSale, numberFormat)}</Box>
        </Stack>
      </Stack>

      <Tooltip title={name} placement="top-start">
        {preview ? (
          <Typography noWrap variant="subtitle2">
            {name}
          </Typography>
        ) : (
          <Link
            noWrap
            color="inherit"
            variant="subtitle2"
            component={RouterLink}
            href={`${paths.product.details(id)}${newSearchParams({ country })}`}
          >
            {name}
          </Link>
        )}
      </Tooltip>

      <ShowMoreLessText text={shortDescription} onClick={setShowMoreLess} sx={{ opacity: 0.8 }} />

      <Box sx={{ gap: 1, display: 'flex' }}>
        <Label
          variant="soft"
          startIcon={<Iconify icon="fluent-color:star-24" />}
          sx={{ opacity: 0.7, color: 'common.white' }}
        >
          {openReviews ? reviewHook.totalRating : (totalRating ?? 0)}
        </Label>

        {newLabel?.enabled && (
          <Label variant="soft" color="info">
            {t('NEW')}
          </Label>
        )}

        {shipping && (
          <Label
            variant="soft"
            color="success"
            startIcon={<Iconify icon="carbon:delivery-parcel" />}
          >
            {t('Free shipping')}
          </Label>
        )}

        {saleLabel?.enabled && (
          <Label variant="soft" color="error">
            {`- ${saleLabel?.content ?? 0}%`}
          </Label>
        )}
      </Box>
    </Stack>
  );
}
